import { useRef, useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import { clearSolMemories, describeMemoryWriteError } from '../../data/writers/sol-memory-writers';
import {
  MEMORY_CLEAR_CANCELLED_TEXT,
  MEMORY_CLEAR_FAILED_TEXT,
  MEMORY_CLEARED_TEXT,
} from './sol-wording';

/**
 * "CLEAR ALL MEMORIES" — the confirmation, and the one tap that does it.
 * M11 / D-040.
 *
 * **NOTHING HAS HAPPENED WHEN THIS APPEARS.** Sol understood the request and
 * Crewlink answered with a question; the clear happens on Confirm, through the
 * memory writer, or it never happens at all. Cancel sends nothing.
 *
 * **A MEMORY WRITE, BEHIND ITS OWN CONFIRMATION — NOT THE OPERATIONAL ONE.**
 * This card is not the Capture confirmation card and does not reuse its
 * endpoint or its ticket: it calls `clear_sol_memories` as the signed-in
 * person, and that function reaches `sol_memory` and nothing else.
 */

export interface SolMemoryClearCardProps {
  readonly activeCount: number;
  readonly disabled?: boolean | undefined;
  /** Reload whatever shows memories, if anything. */
  readonly onCleared?: (() => void) | undefined;
}

type CardState =
  | { readonly kind: 'offered' }
  | { readonly kind: 'clearing' }
  | { readonly kind: 'done'; readonly text: string }
  | { readonly kind: 'stopped'; readonly text: string };

export function SolMemoryClearCard({ activeCount, disabled, onCleared }: SolMemoryClearCardProps) {
  const [state, setState] = useState<CardState>({ kind: 'offered' });
  const inFlight = useRef(false);

  const confirm = (): void => {
    if (inFlight.current || state.kind !== 'offered' || disabled === true) return;
    const client = getSupabaseClient();
    if (!client) {
      setState({ kind: 'stopped', text: MEMORY_CLEAR_FAILED_TEXT });
      return;
    }
    inFlight.current = true;
    setState({ kind: 'clearing' });
    void clearSolMemories(client)
      .then(() => {
        setState({ kind: 'done', text: MEMORY_CLEARED_TEXT });
        onCleared?.();
      })
      .catch((caught: unknown) => {
        const message = caught instanceof Error ? caught.message : String(caught);
        setState({ kind: 'stopped', text: describeMemoryWriteError(message) });
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  if (state.kind === 'done') {
    return <p className="sol-action-done">{state.text}</p>;
  }
  if (state.kind === 'stopped') {
    return <p className="sol-action-stopped">{state.text}</p>;
  }

  const label = activeCount === 1 ? 'Clear 1 memory' : `Clear all ${activeCount} memories`;
  return (
    <div className="sol-action" role="group" aria-label="Clear all memories">
      <p className="sol-action-prompt">This forgets everything Sol remembers for you.</p>
      <div className="sol-action-buttons">
        <button
          type="button"
          className="sol-action-confirm"
          onClick={confirm}
          disabled={state.kind === 'clearing' || disabled === true}
        >
          {state.kind === 'clearing' ? 'Clearing…' : label}
        </button>
        <button
          type="button"
          className="sol-action-cancel"
          onClick={() => setState({ kind: 'stopped', text: MEMORY_CLEAR_CANCELLED_TEXT })}
          disabled={state.kind === 'clearing'}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
